<template>
    <div>
        <OfferRedirectModal
            :show="showModal"
            :settings="widgetSetting"
            @closeModal="closeModal"
            @confirmModal="confirmModal"
        />

        <div class="container">
            <PrimaryHeader class="p-0" />
        </div>

        <!-- Error page can load without global page data -->
        <div v-if="showErrorPage">
            <ErrorPageContent />
        </div>

        <div v-else class="container">
            <h1 class="title">{{ $t('title') }}</h1>

            <IndependentOfferBlock
                ref="offerBlock"
                class="independent-offer-block"
                :offers="offers"
                :offerOrder="pageData?.offer_order"
                :annualLinkTarget="pageData?.content?.annualLinkTarget"
                :ctaLinkTarget="pageData?.content?.CTALinkTarget"
                :applicationId="applicationId"
                :identifier="identifier"
                :partnerCustomerReference="partnerCustomerReference"
                :widgetColors="widgetColors"
                :submitType="widgetSetting.widget_speed_bump_type"
                @submitOffer="submitOffer"
            />

            <div class="d-flex justify-content-center">
                <a class="noThanks" @click="closeWindow()" v-html="$t('noThanks')" />
            </div>

            <div class="pt-4">
                <!--Short Opt Out-->
                <div class="optout desktop" v-html="$t('optout')"></div>
                <br />

                <!--Global-->
                <div class="global" v-html="$t('global')"></div>

                <div class="pqDisclosure">
                    <p v-if="$te('pqdisclosure.title')" v-html="$t('pqdisclosure.title')"></p>

                    <p v-if="$te('pqdisclosure.*')" v-html="$t('pqdisclosure.*')"></p>

                    <p v-for="count in pqdisclosureCount" v-html="$t('pqdisclosure.' + count)"></p>
                </div>

                <div class="optOutLong" v-html="$t('optoutlong')"></div>
            </div>
        </div>

        <IndependentFooter />
    </div>
</template>

<script>
import { ref } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { declineLog } from '@js/utilities/shroom'
import fetchWidgetData from '@js/utilities/widget'

import isEmpty from 'lodash/isEmpty'

export default {
    props: ['offers', 'pageData', 'applicationData', 'loaded', 'expired'],
    data() {
        return {
            widgetTranslation: {},
            widgetColors: {},
            widgetSetting: {},
            widgetLink: '',
            widgetLinkHover: '',
            widgetDataLoaded: false,
            showModal: false,
            offer: false,
            offerIndex: false,
        }
    },
    setup(props, { expose }) {
        const store = useStore()
        const router = useRouter()

        const isIndependentLoaded = ref(false)

        expose({ isIndependentLoaded: isIndependentLoaded })

        return {
            router,
            store,
            isIndependentLoaded,
        }
    },
    computed: {
        showErrorPage: function () {
            if (this.store.state.global.data.showErrorPage && this.pageData?.content?.noofferurl) {
                window.location.replace(this.pageData?.content?.noofferurl)
            }

            return this.store.state.global.data.showErrorPage
        },
        pqdisclosureCount: function () {
            let count = 1

            while (count > -1) {
                if (!this.$te('pqdisclosure.' + count)) {
                    count--
                    break
                }

                count++
            }

            return count
        },
        applicationId: function () {
            return this.applicationData?.reference
        },
        identifier: function () {
            return this.applicationData?.v6 ?? ''
        },
        partnerCustomerReference: function () {
            return this.applicationData?.v1
        },
    },
    methods: {
        closeWindow: async function () {
            // track shroom decline offers
            await declineLog(this.partnerCustomerReference, this.identifier, this.applicationId)

            window.close()
        },
        async setWidget(identifier) {
            const widget = await fetchWidgetData(identifier)

            if (widget) {
                this.widgetColors = widget.colors
                this.widgetTranslation = widget.translations
                this.widgetSetting = widget.pageSettings
            }
        },
        closeModal: function (close) {
            this.showModal = close
            this.offer = false
            this.offerIndex = false
        },
        submitOffer: function (showOfferModal, offer, index) {
            this.showModal = showOfferModal
            this.offer = offer
            this.offerIndex = index
        },
        confirmModal: function (close) {
            this.showModal = close
            this.$refs.offerBlock.goToOffer(this.offer, this.offerIndex)
        },
    },
    watch: {
        async loaded(newValue) {
            if (!this.widgetDataLoaded && newValue === true) {
                await this.setWidget(this.identifier)

                if (!isEmpty(this.widgetTranslation)) {
                    this.$i18n.mergeLocaleMessage(this.$i18n.locale, this.widgetTranslation)
                }

                this.widgetDataLoaded = true
                this.isIndependentLoaded = true
            }
        },
    },
}
</script>

<style lang="scss">
@import '~scss/mixins/rem';
@import '~scss/settings/responsive';

.submitButton {
    background: v-bind('widgetColors.widget_button_background_primary');
    border: 1px solid v-bind('widgetColors.widget_button_border_primary');
    border-radius: v-bind('widgetColors.widget_button_border_radius');
    color: v-bind('widgetColors.widget_button_text_primary');
    box-shadow: none;
    width: 50%;
    text-align: center;

    &:hover,
    &[disabled]:hover,
    &:focus,
    &[disabled]:focus {
        background-color: v-bind('widgetColors.widget_button_background_hover_primary');
        border-color: v-bind('widgetColors.widget_button_border_hover_primary');
        color: v-bind('widgetColors.widget_button_text_hover_primary');
        background-position: 0;
        box-shadow: none;
        text-decoration: none;
    }

    @media (max-width: 1199px) {
        width: 60%;
        text-align: center;
    }
}

.container {
    .title {
        margin-top: 50px;
        color: #19398a;
        font-size: 52px;
        font-family: Arial, sans-serif;
        line-height: 57px;
        font-weight: normal;

        @media (max-width: 767px) {
            font-size: 30px;
            line-height: 40px;
        }
    }

    .independent-offer-block {
        padding: 0 0 40px 0;
    }

    .noThanks {
        cursor: pointer;
        transition: all 0.2s ease-in-out;
        color: v-bind('widgetColors.widget_link');

        &:hover {
            transform: scale(1.2);
            color: v-bind('widgetColors.widget_link_hover');
        }
    }

    .optout {
        font-size: 1.3em;
        font-weight: 400;
        border: 1px solid #000;
        padding: 15px;
        font-style: italic;

        @media (max-width: 767px) {
            font-size: 1em;
        }

        &.desktop {
            display: block;

            @media (max-width: $bp-medium) {
                display: none;
            }
        }
    }

    .global {
        font-family: Arial, sans-serif;

        &input,
        &textarea,
        &.uneditable-input,
        &button,
        &select,
        &textarea,
        .global {
            font-family: Arial, sans-serif;
        }

        p {
            font-family: Arial, sans-serif;
            font-size: 1em;

            @media (max-width: 767px) {
                font-size: 0.8em;
            }
        }
    }

    .pqDisclosure {
        @extend .global;

        p {
            color: #535353;
        }
    }

    .optout {
        @extend .global;

        p {
            color: #535353;
        }
    }

    .optOutLong {
        p {
            font-size: 1.3em;
            font-weight: 600;
            padding: 30px;
            color: #535353;
            font-family: Arial, sans-serif;
            word-wrap: break-word;

            @media (max-width: 767px) {
                font-size: 1em;
            }
        }
    }

    a {
        color: #19398a;
        text-decoration: none;

        &.visited {
            color: #19398a;
        }
    }

    .sup {
        &.pointer {
            cursor: pointer;
        }
    }
}
</style>
